import React, { useState } from 'react';
import {
  Box,
  Typography,
  FormControlLabel,
  RadioGroup,
  Radio,
  Select,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
  Switch,
  Slider,
  FormControl,
  InputLabel,
  useTheme,
  alpha,
  Grid,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import {
  Palette as PaletteIcon,
  Language as LanguageIcon,
  TextFields as TextFieldsIcon,
  Accessibility as AccessibilityIcon,
  Check as CheckIcon,
  Schedule as ScheduleIcon,
  FormatSize as FormatSizeIcon,
  SpaceBar as SpaceBarIcon,
} from '@mui/icons-material';
import { useSettingsStore } from '../../stores/settingsStore';
import { useThemeStore, themePalettes } from '../../stores/themeStore';

const SystemSettings: React.FC = () => {
  const muiTheme = useTheme();
  const isDark = muiTheme.palette.mode === 'dark';
  const { settings, isLoading, isSaving, updateSettings } = useSettingsStore();
  const { setMode: setThemeMode, setDensityMode: setThemeDensity, setAccentColor: setThemeAccentColor, selectedPalette, setSelectedPalette, densityMode } = useThemeStore();
  // Derived helpers for live preview — use palette.mode so preview is instant
  const currentPalette = themePalettes.find(p => p.id === selectedPalette) || themePalettes[0];
  const previewDark = currentPalette.mode === 'dark';
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ 
    open: false, 
    message: '', 
    severity: 'success' 
  });

  const handleSettingChange = async (key: string, value: any) => {
    try {
      await updateSettings({ [key]: value });
      
      if (key === 'theme') {
        setSelectedPalette(value); // setSelectedPalette now also updates actualMode
      }
      if (key === 'displayDensity') {
        setThemeDensity(value as 'comfortable' | 'cozy' | 'compact');
      }
      if (key === 'accentColor') {
        setThemeAccentColor(value as 'blue' | 'green' | 'purple' | 'orange' | 'red' | 'teal');
      }
      
      setSnackbar({ open: true, message: 'Settings saved', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Failed to save settings', severity: 'error' });
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  const languages = [
    { code: 'en', label: 'English', flag: '🇺🇸' },
    { code: 'es', label: 'Spanish', flag: '🇪🇸' },
    { code: 'fr', label: 'French', flag: '🇫🇷' },
    { code: 'de', label: 'German', flag: '🇩🇪' },
    { code: 'hi', label: 'Hindi', flag: '🇮🇳' },
    { code: 'zh', label: 'Chinese', flag: '🇨🇳' },
    { code: 'ja', label: 'Japanese', flag: '🇯🇵' },
    { code: 'ar', label: 'Arabic', flag: '🇸🇦' },
    { code: 'pt', label: 'Portuguese', flag: '🇧🇷' },
    { code: 'ru', label: 'Russian', flag: '🇷🇺' },
  ];

  const timezones = [
    { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
    { value: 'America/New_York', label: 'Eastern Time (US & Canada)' },
    { value: 'America/Chicago', label: 'Central Time (US & Canada)' },
    { value: 'America/Denver', label: 'Mountain Time (US & Canada)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (US & Canada)' },
    { value: 'Europe/London', label: 'London (GMT)' },
    { value: 'Europe/Paris', label: 'Paris (CET)' },
    { value: 'Europe/Berlin', label: 'Berlin (CET)' },
    { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
    { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
    { value: 'Asia/Kolkata', label: 'India (IST)' },
    { value: 'Australia/Sydney', label: 'Sydney (AEDT)' },
  ];

  const fontFamilies = [
    { value: 'Inter', label: 'Inter', sample: 'Modern & Clean' },
    { value: 'Roboto', label: 'Roboto', sample: 'Material Design' },
    { value: 'Open Sans', label: 'Open Sans', sample: 'Friendly & Readable' },
    { value: 'Arial', label: 'Arial', sample: 'Classic & Universal' },
    { value: 'Georgia', label: 'Georgia', sample: 'Elegant Serif' },
  ];

  // Section Card Component
  const SettingsCard = ({ 
    icon, 
    title, 
    description, 
    children 
  }: { 
    icon: React.ReactNode; 
    title: string; 
    description?: string;
    children: React.ReactNode;
  }) => (
    <Card 
      elevation={0}
      sx={{ 
        mb: 3, 
        border: `1px solid ${isDark ? alpha('#fff', 0.1) : alpha('#000', 0.08)}`,
        borderRadius: 3,
        overflow: 'visible',
        bgcolor: isDark ? alpha('#fff', 0.02) : '#fff',
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2.5 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              bgcolor: alpha(muiTheme.palette.primary.main, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'primary.main',
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 0.25 }}>
              {title}
            </Typography>
            {description && (
              <Typography variant="body2" color="text.secondary">
                {description}
              </Typography>
            )}
          </Box>
        </Box>
        {children}
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ maxWidth: 800 }}>
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={2000} 
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Typography variant="h4" fontWeight={700}>
            System Settings
          </Typography>
          {isSaving && <CircularProgress size={20} />}
        </Box>
        <Typography variant="body1" color="text.secondary">
          Customize your CruvzMail experience with themes, language, and accessibility options
        </Typography>
      </Box>

      {/* Theme Selection */}
      <SettingsCard
        icon={<PaletteIcon />}
        title="Theme"
        description="Choose how CruvzMail looks to you"
      >
        {/* Theme Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {themePalettes.map((palette) => {
            const isSelected = selectedPalette === palette.id;
            return (
              <Grid size={{ xs: 6, sm: 4 }} key={palette.id}>
                <Box
                  onClick={() => {
                    setSelectedPalette(palette.id);
                    // Persist palette choice to backend (setSelectedPalette already updates the MUI mode)
                    updateSettings({ theme: palette.id }).catch(() => {});
                  }}
                  sx={{
                    cursor: 'pointer',
                    borderRadius: 2.5,
                    overflow: 'hidden',
                    border: isSelected 
                      ? `2px solid ${muiTheme.palette.primary.main}`
                      : `2px solid ${isDark ? alpha('#fff', 0.1) : alpha('#000', 0.1)}`,
                    transition: 'all 0.2s ease',
                    transform: isSelected ? 'scale(1.02)' : 'scale(1)',
                    boxShadow: isSelected ? `0 8px 24px ${alpha(muiTheme.palette.primary.main, 0.25)}` : 'none',
                    '&:hover': {
                      borderColor: muiTheme.palette.primary.main,
                      transform: 'scale(1.02)',
                    },
                  }}
                >
                  {/* Preview */}
                  <Box sx={{ display: 'flex', height: 80 }}>
                    <Box 
                      sx={{ 
                        width: '35%', 
                        bgcolor: palette.dark,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        pt: 1.5,
                        gap: 0.5,
                      }}
                    >
                      <Box sx={{ width: 16, height: 16, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.2)' }} />
                      <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: 'rgba(255,255,255,0.4)' }} />
                      <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: 'rgba(255,255,255,0.4)' }} />
                    </Box>
                    <Box 
                      sx={{ 
                        width: '65%', 
                        bgcolor: palette.light,
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {isSelected && (
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            bgcolor: 'primary.main',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: 2,
                          }}
                        >
                          <CheckIcon sx={{ color: '#fff', fontSize: 18 }} />
                        </Box>
                      )}
                    </Box>
                  </Box>
                  
                  {/* Label */}
                  <Box sx={{ p: 1.5, bgcolor: isDark ? alpha('#fff', 0.03) : '#fafafa' }}>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.25 }}>
                      {palette.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3 }}>
                      {palette.description}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            );
          })}
        </Grid>

        {/* Live Preview */}
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main' }} />
            Live Preview
          </Typography>
          <Box
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              border: `1px solid ${isDark ? alpha('#fff', 0.1) : '#e0e0e0'}`,
              maxWidth: 420,
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            }}
          >
            {/* Browser Chrome */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 1, bgcolor: isDark ? '#1a1a1a' : '#f5f5f5', borderBottom: `1px solid ${isDark ? '#333' : '#e0e0e0'}` }}>
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ff5f57' }} />
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ffbd2e' }} />
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#28c940' }} />
              <Box sx={{ flex: 1, mx: 2 }}>
                <Box sx={{ bgcolor: isDark ? '#333' : '#fff', borderRadius: 1.5, py: 0.5, px: 1.5, border: `1px solid ${isDark ? '#444' : '#e0e0e0'}` }}>
                  <Typography sx={{ fontSize: 10, color: 'text.secondary', fontWeight: 500 }}>cruvzmail.com</Typography>
                </Box>
              </Box>
            </Box>

            {/* App Preview - Hybrid Design */}
            <Box sx={{ display: 'flex', height: 180 }}>
              {/* Hybrid Sidebar */}
              <Box
                sx={{
                  width: 110,
                  bgcolor: previewDark ? currentPalette.sidebar : '#ffffff',
                  borderRight: `1px solid ${previewDark ? 'rgba(255,255,255,0.08)' : '#e5e7eb'}`,
                  display: 'flex',
                  flexDirection: 'column',
                  py: 1,
                  px: 0.75,
                }}
              >
                {/* Compose button */}
                <Box sx={{
                  bgcolor: '#1976d2',
                  borderRadius: 4,
                  py: 0.5, px: 1, mb: 1,
                  display: 'flex', alignItems: 'center', gap: 0.5,
                }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.5)' }} />
                  <Typography sx={{ fontSize: 8, color: '#fff', fontWeight: 700 }}>Compose</Typography>
                </Box>
                {/* Folder list */}
                {[
                  { name: 'Inbox', active: true },
                  { name: 'Sent', active: false },
                  { name: 'Drafts', active: false },
                  { name: 'Spam', active: false },
                ].map((item) => (
                  <Box key={item.name} sx={{
                    py: 0.35, px: 0.5, borderRadius: 0.75, mb: 0.2,
                    bgcolor: item.active ? alpha('#1976d2', previewDark ? 0.25 : 0.1) : 'transparent',
                    display: 'flex', alignItems: 'center', gap: 0.5,
                  }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: 0.5, bgcolor: item.active ? '#1976d2' : (previewDark ? '#4b5563' : '#9ca3af') }} />
                    <Typography sx={{ fontSize: 8, color: item.active ? '#1976d2' : (previewDark ? '#94a3b8' : '#374151'), fontWeight: item.active ? 700 : 400 }}>
                      {item.name}
                    </Typography>
                  </Box>
                ))}
                <Box sx={{ flex: 1 }} />
                {/* App icons row at bottom */}
                <Box sx={{ display: 'flex', gap: '3px', pt: 0.5, borderTop: `1px solid ${previewDark ? 'rgba(255,255,255,0.08)' : '#e5e7eb'}` }}>
                  {[
                    { bg: 'linear-gradient(135deg, #06b6d4, #3b82f6)' },
                    { bg: 'linear-gradient(135deg, #f97316, #ef4444)' },
                    { bg: 'linear-gradient(135deg, #059669, #047857)' },
                    { bg: 'linear-gradient(135deg, #6366f1, #4f46e5)' },
                  ].map((a, i) => (
                    <Box key={i} sx={{ width: 14, height: 14, borderRadius: '4px', background: a.bg, boxShadow: '0 1px 4px rgba(0,0,0,0.25)' }} />
                  ))}
                </Box>
              </Box>

              {/* Content Area */}
              <Box sx={{ flex: 1, bgcolor: previewDark ? '#111827' : '#f8fafc', p: 0.75 }}>
                <Box sx={{ bgcolor: previewDark ? '#1f2937' : '#fff', borderRadius: 1.5, height: '100%', p: 0.75, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75, pb: 0.5, borderBottom: `1px solid ${previewDark ? '#374151' : '#f0f0f0'}` }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: previewDark ? '#e5e7eb' : '#111827' }}>Inbox</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: previewDark ? '#374151' : '#f0f0f0' }} />
                      <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: previewDark ? '#374151' : '#f0f0f0' }} />
                    </Box>
                  </Box>
                  {[
                    { color: '#3b82f6', from: 'John Doe', subject: 'Meeting tomorrow', time: '10:30' },
                    { color: '#10b981', from: 'Marketing', subject: 'Weekly report', time: '09:15' },
                    { color: '#f59e0b', from: 'Support', subject: 'Ticket #1234', time: '08:45' },
                    { color: '#8b5cf6', from: 'Newsletter', subject: 'Latest updates', time: 'Yesterday' },
                  ].map((email, i) => (
                    <Box key={i} sx={{ 
                      display: 'flex', 
                      gap: 0.75, 
                      mb: 0.75, 
                      alignItems: 'center',
                      py: 0.5,
                      px: 0.5,
                      borderRadius: 1,
                      bgcolor: i === 0 ? (previewDark ? alpha('#3b82f6', 0.15) : alpha('#3b82f6', 0.08)) : 'transparent',
                    }}>
                      <Box sx={{ 
                        width: 20, height: 20, borderRadius: '50%', 
                        bgcolor: email.color + '22',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: email.color }} />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 9, fontWeight: 600, color: 'text.primary', lineHeight: 1.2 }}>{email.from}</Typography>
                        <Typography sx={{ fontSize: 8, color: 'text.secondary', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{email.subject}</Typography>
                      </Box>
                      <Typography sx={{ fontSize: 7, color: 'text.secondary', flexShrink: 0 }}>{email.time}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </SettingsCard>

      {/* Display Density */}
      <SettingsCard
        icon={<SpaceBarIcon />}
        title="Display Density"
        description="Control how compact the email list appears"
      >
        <Grid container spacing={2}>
          {[
            { value: 'comfortable', label: 'Comfortable', description: 'Spacious rows — easy to read', rows: 3 },
            { value: 'cozy', label: 'Cozy', description: 'Balanced spacing', rows: 4 },
            { value: 'compact', label: 'Compact', description: 'Dense rows — see more at once', rows: 6 },
          ].map((option) => {
            const isActive = (settings.displayDensity || 'comfortable') === option.value;
            return (
              <Grid size={{ xs: 12, sm: 4 }} key={option.value}>
                <Box
                  onClick={() => handleSettingChange('displayDensity', option.value)}
                  sx={{
                    cursor: 'pointer',
                    borderRadius: 2.5,
                    overflow: 'hidden',
                    border: isActive
                      ? `2px solid ${muiTheme.palette.primary.main}`
                      : `2px solid ${isDark ? alpha('#fff', 0.1) : alpha('#000', 0.1)}`,
                    transition: 'all 0.2s ease',
                    transform: isActive ? 'scale(1.02)' : 'scale(1)',
                    boxShadow: isActive ? `0 8px 24px ${alpha(muiTheme.palette.primary.main, 0.25)}` : 'none',
                    '&:hover': { borderColor: muiTheme.palette.primary.main, transform: 'scale(1.02)' },
                  }}
                >
                  {/* Density preview */}
                  <Box sx={{ p: 1.5, bgcolor: isDark ? alpha('#fff', 0.03) : '#f5f7fa', minHeight: 80 }}>
                    {Array.from({ length: option.rows }).map((_, i) => (
                      <Box
                        key={i}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.75,
                          py: option.value === 'compact' ? 0.2 : option.value === 'cozy' ? 0.4 : 0.65,
                          borderBottom: i < option.rows - 1 ? `1px solid ${isDark ? alpha('#fff', 0.06) : alpha('#000', 0.06)}` : 'none',
                        }}
                      >
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4'][i % 6] + '44', flexShrink: 0 }} />
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ height: option.value === 'compact' ? 4 : 5, borderRadius: 0.5, bgcolor: isDark ? alpha('#fff', 0.2) : alpha('#000', 0.15), mb: 0.3 }} />
                          <Box sx={{ height: 3, borderRadius: 0.5, bgcolor: isDark ? alpha('#fff', 0.1) : alpha('#000', 0.08), width: '60%' }} />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                  <Box sx={{ p: 1.5, bgcolor: isDark ? alpha('#fff', 0.02) : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>{option.label}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>{option.description}</Typography>
                    </Box>
                    {isActive && (
                      <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <CheckIcon sx={{ color: '#fff', fontSize: 14 }} />
                      </Box>
                    )}
                  </Box>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </SettingsCard>

      {/* Language & Region */}
      <SettingsCard
        icon={<LanguageIcon />}
        title="Language & Region"
        description="Set your preferred language and regional settings"
      >
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Language</InputLabel>
              <Select
                value={settings.language || 'en'}
                label="Language"
                onChange={(e) => handleSettingChange('language', e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                {languages.map((lang) => (
                  <MenuItem key={lang.code} value={lang.code}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Typography sx={{ fontSize: 18 }}>{lang.flag}</Typography>
                      <Typography>{lang.label}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Timezone</InputLabel>
              <Select
                value={settings.timezone || 'UTC'}
                label="Timezone"
                onChange={(e) => handleSettingChange('timezone', e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                {timezones.map((tz) => (
                  <MenuItem key={tz.value} value={tz.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ScheduleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2">{tz.label}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="subtitle2" fontWeight={500} sx={{ mb: 1.5 }}>Date Format</Typography>
            <RadioGroup
              value={settings.dateFormat || 'MM/DD/YYYY'}
              onChange={(e) => handleSettingChange('dateFormat', e.target.value)}
            >
              {[
                { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY', example: '01/12/2026' },
                { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY', example: '12/01/2026' },
                { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD', example: '2026-01-12' },
              ].map((format) => (
                <FormControlLabel
                  key={format.value}
                  value={format.value}
                  control={<Radio size="small" />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Typography variant="body2">{format.label}</Typography>
                      <Chip label={format.example} size="small" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
                    </Box>
                  }
                  sx={{ mb: 0.5 }}
                />
              ))}
            </RadioGroup>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="subtitle2" fontWeight={500} sx={{ mb: 1.5 }}>Time Format</Typography>
            <RadioGroup
              value={settings.timeFormat || '12h'}
              onChange={(e) => handleSettingChange('timeFormat', e.target.value)}
            >
              {[
                { value: '12h', label: '12-hour', example: '2:30 PM' },
                { value: '24h', label: '24-hour', example: '14:30' },
              ].map((format) => (
                <FormControlLabel
                  key={format.value}
                  value={format.value}
                  control={<Radio size="small" />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Typography variant="body2">{format.label}</Typography>
                      <Chip label={format.example} size="small" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
                    </Box>
                  }
                  sx={{ mb: 0.5 }}
                />
              ))}
            </RadioGroup>
          </Grid>
        </Grid>
      </SettingsCard>

      {/* Typography */}
      <SettingsCard
        icon={<TextFieldsIcon />}
        title="Typography"
        description="Customize fonts and text display"
      >
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Font Family</InputLabel>
              <Select
                value={settings.fontFamily || 'Inter'}
                label="Font Family"
                onChange={(e) => handleSettingChange('fontFamily', e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                {fontFamilies.map((font) => (
                  <MenuItem key={font.value} value={font.value}>
                    <Box>
                      <Typography sx={{ fontFamily: font.value }}>{font.label}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: font.value }}>
                        {font.sample}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <FormatSizeIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Typography variant="subtitle2" fontWeight={500}>Font Size</Typography>
            </Box>
            <Box sx={{ px: 1 }}>
              <Slider
                value={settings.fontSize || 14}
                onChange={(_, value) => handleSettingChange('fontSize', value)}
                min={12}
                max={18}
                step={1}
                marks={[
                  { value: 12, label: 'S' },
                  { value: 14, label: 'M' },
                  { value: 16, label: 'L' },
                  { value: 18, label: 'XL' },
                ]}
                valueLabelDisplay="auto"
                sx={{ mt: 1 }}
              />
            </Box>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <SpaceBarIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Typography variant="subtitle2" fontWeight={500}>Line Spacing</Typography>
            </Box>
            <Box sx={{ px: 1 }}>
              <Slider
                value={settings.lineSpacing || 1.5}
                onChange={(_, value) => handleSettingChange('lineSpacing', value)}
                min={1}
                max={2}
                step={0.1}
                marks={[
                  { value: 1, label: 'Tight' },
                  { value: 1.5, label: 'Normal' },
                  { value: 2, label: 'Relaxed' },
                ]}
                valueLabelDisplay="auto"
                sx={{ mt: 1 }}
              />
            </Box>
          </Grid>
        </Grid>
      </SettingsCard>

      {/* Accessibility */}
      <SettingsCard
        icon={<AccessibilityIcon />}
        title="Accessibility"
        description="Make CruvzMail easier to use"
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            p: 2,
            borderRadius: 2,
            bgcolor: isDark ? alpha('#fff', 0.03) : alpha('#000', 0.02),
          }}>
            <Box>
              <Typography variant="subtitle2" fontWeight={500}>High Contrast Mode</Typography>
              <Typography variant="caption" color="text.secondary">
                Increase contrast for better visibility
              </Typography>
            </Box>
            <Switch
              checked={settings.highContrast || false}
              onChange={(e) => handleSettingChange('highContrast', e.target.checked)}
            />
          </Box>

          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            p: 2,
            borderRadius: 2,
            bgcolor: isDark ? alpha('#fff', 0.03) : alpha('#000', 0.02),
          }}>
            <Box>
              <Typography variant="subtitle2" fontWeight={500}>Reduce Motion</Typography>
              <Typography variant="caption" color="text.secondary">
                Minimize animations and transitions
              </Typography>
            </Box>
            <Switch
              checked={settings.reduceMotion || false}
              onChange={(e) => handleSettingChange('reduceMotion', e.target.checked)}
            />
          </Box>

          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            p: 2,
            borderRadius: 2,
            bgcolor: isDark ? alpha('#fff', 0.03) : alpha('#000', 0.02),
          }}>
            <Box>
              <Typography variant="subtitle2" fontWeight={500}>Keyboard Shortcuts</Typography>
              <Typography variant="caption" color="text.secondary">
                Enable keyboard navigation shortcuts
              </Typography>
            </Box>
            <Switch
              checked={settings.enableKeyboardShortcuts !== false}
              onChange={(e) => handleSettingChange('enableKeyboardShortcuts', e.target.checked)}
            />
          </Box>

          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            p: 2,
            borderRadius: 2,
            bgcolor: isDark ? alpha('#fff', 0.03) : alpha('#000', 0.02),
          }}>
            <Box>
              <Typography variant="subtitle2" fontWeight={500}>Screen Reader Optimization</Typography>
              <Typography variant="caption" color="text.secondary">
                Optimize for assistive technologies
              </Typography>
            </Box>
            <Switch
              checked={settings.screenReaderOptimized || false}
              onChange={(e) => handleSettingChange('screenReaderOptimized', e.target.checked)}
            />
          </Box>
        </Box>
      </SettingsCard>
    </Box>
  );
};

export default SystemSettings;
